import { PaymentsConfig, PaymentProviderType } from '../shared/types/config';
import { PaymentsError, PaymentErrorType } from '../shared/types/errors';
import { PaymentProvider } from '../shared/types/provider';
import { StripePaymentProvider } from './stripe';
// import { PayPalPaymentProvider } from './paypal';
// import { ApplePayPaymentProvider } from './applepay';
import { MockPaymentProvider } from './mock';

export class PaymentProviderFactory {
  public static create(config: PaymentsConfig): PaymentProvider {
    switch (config.provider) {
      case 'stripe':
        return new StripePaymentProvider(config);
      case 'paypal':
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          'PayPal provider is not yet available in this build'
        );
      case 'applepay':
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          'Apple Pay provider is not yet available in this build'
        );
      case 'mock':
        return new MockPaymentProvider(config);
      default:
        throw new PaymentsError(
          PaymentErrorType.CONFIGURATION_ERROR,
          `Unsupported payment provider: ${config.provider}`
        );
    }
  }

  public static autodetect(): PaymentsConfig {
    // Check for Stripe configuration first (most common)
    if (
      process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY &&
      process.env.STRIPE_SECRET_KEY
    ) {
      return {
        provider: 'stripe',
        publishableKey: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
        environment:
          process.env.NODE_ENV === 'production' ? 'production' : 'development',
        stripeConfig: {
          publishableKey: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
        },
      };
    }

    // Check for PayPal configuration
    if (
      process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID &&
      process.env.PAYPAL_CLIENT_SECRET
    ) {
      return {
        provider: 'paypal',
        publishableKey: process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID,
        environment:
          process.env.NODE_ENV === 'production' ? 'production' : 'development',
        paypalConfig: {
          clientId: process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID,
          clientSecret: process.env.PAYPAL_CLIENT_SECRET,
          environment:
            process.env.NODE_ENV === 'production' ? 'production' : 'sandbox',
          currency: process.env.PAYPAL_CURRENCY || 'USD',
        },
      };
    }

    // Check for Apple Pay configuration
    if (
      process.env.NEXT_PUBLIC_APPLE_PAY_MERCHANT_ID &&
      typeof window !== 'undefined' &&
      window.ApplePaySession
    ) {
      return {
        provider: 'applepay',
        environment:
          process.env.NODE_ENV === 'production' ? 'production' : 'development',
        applePayConfig: {
          merchantId: process.env.NEXT_PUBLIC_APPLE_PAY_MERCHANT_ID,
          merchantName:
            process.env.NEXT_PUBLIC_APPLE_PAY_MERCHANT_NAME || 'Your Store',
          countryCode: process.env.NEXT_PUBLIC_APPLE_PAY_COUNTRY_CODE || 'US',
          currencyCode:
            process.env.NEXT_PUBLIC_APPLE_PAY_CURRENCY_CODE || 'USD',
          environment:
            process.env.NODE_ENV === 'production' ? 'production' : 'sandbox',
        },
      };
    }

    // For development/testing, allow mock provider if no other is detected
    if (
      process.env.NODE_ENV !== 'production' ||
      process.env.VERCEL_ENV === 'preview'
    ) {
      console.warn(
        'No payment provider detected, defaulting to MockPaymentProvider for development/preview environment.'
      );
      return {
        provider: 'mock',
        environment: 'development',
      };
    }

    throw new PaymentsError(
      PaymentErrorType.CONFIGURATION_ERROR,
      'Could not auto-detect payment provider. Please provide explicit configuration.'
    );
  }

  // Helper method to get all available providers based on environment
  public static getAvailableProviders(): PaymentProviderType[] {
    const providers: PaymentProviderType[] = [];

    // Check Stripe
    if (process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY) {
      providers.push('stripe');
    }

    // Check PayPal
    if (process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID) {
      providers.push('paypal');
    }

    // Check Apple Pay (client-side only)
    if (
      typeof window !== 'undefined' &&
      window.ApplePaySession?.canMakePayments()
    ) {
      providers.push('applepay');
    }

    // Always include mock for development
    if (process.env.NODE_ENV !== 'production') {
      providers.push('mock');
    }

    return providers;
  }

  // Method to create multiple providers for fallback scenarios
  public static createMultiple(configs: PaymentsConfig[]): PaymentProvider[] {
    return configs.map(config => this.create(config));
  }
}
